/**
 * Interpretação da listagem de diretório (nginx autoindex) de um produto do
 * STAR. Cada linha útil tem a forma:
 *
 *   <a href="20262580020_GOES19-ABI-nsa-GEOCOLOR-1800x1080.jpg">…</a>   15-Sep-2026 00:34   896452
 */
import { isImageWidth, imageHeight, type ImageWidth } from './star';
import { parseStarStamp } from './stamp';

export interface ListingFrame {
  /** Instante do quadro (epoch ms, UTC). */
  time: number;
  /** Tamanhos publicados para este quadro → bytes do arquivo (NaN se desconhecido). */
  bytes: Partial<Record<ImageWidth, number>>;
}

export interface Listing {
  productId: string;
  /** Quadros em ordem crescente de tempo. */
  frames: ListingFrame[];
  /** Instantes dos quadros (mesma ordem de `frames`). */
  times: number[];
}

const LINE_RE =
  /href="(\d{11})_GOES19-ABI-nsa-([A-Za-z0-9]+)-(\d+)x(\d+)\.jpg"(?:[^\n]*?<\/a>[ \t]+\S+[ \t]+\S+[ \t]+(\d+))?/g;

/** Interpreta o HTML da listagem. Ignora arquivos de outros produtos e tamanhos desconhecidos. */
export function parseListing(html: string, productId: string): Listing {
  const byTime = new Map<number, ListingFrame>();
  for (const m of html.matchAll(LINE_RE)) {
    const [, stamp, prod, w, h, size] = m;
    if (prod !== productId) continue;
    const width = Number(w);
    if (!isImageWidth(width) || Number(h) !== imageHeight(width)) continue;
    const time = parseStarStamp(stamp);
    if (time === null) continue;
    let frame = byTime.get(time);
    if (!frame) {
      frame = { time, bytes: {} };
      byTime.set(time, frame);
    }
    frame.bytes[width] = size !== undefined ? Number(size) : NaN;
  }
  const frames = [...byTime.values()].sort((a, b) => a.time - b.time);
  return { productId, frames, times: frames.map((f) => f.time) };
}

/**
 * Escolhe o tamanho publicado mais adequado para um quadro: o pedido, se
 * existir; senão o maior abaixo; senão o menor acima.
 */
export function pickAvailableWidth(
  bytes: Partial<Record<ImageWidth, number>>,
  wanted: ImageWidth,
): ImageWidth | null {
  if (bytes[wanted] !== undefined) return wanted;
  const available = (Object.keys(bytes).map(Number) as ImageWidth[]).sort((a, b) => a - b);
  if (available.length === 0) return null;
  const below = available.filter((w) => w < wanted);
  if (below.length) return below[below.length - 1];
  return available[0];
}
